import type { ProtoWallet } from '@ducat-unit/client-sdk';
import type { ChainNetwork } from '@ducat-unit/core';

import type { WalletAccountSnapshot } from '@/context/wallet-account';
import { walletAccountSnapshotsEqual } from '@/context/wallet-account';

import { waitForWalletRead } from './wallet-read-request';

export type WalletActionStage =
  | 'intent'
  | 'reservation'
  | 'construction'
  | 'authorization'
  | 'signing'
  | 'submission'
  | 'broadcast'
  | 'api-request'
  | 'registration';

export type WalletActionInvalidationReason =
  | 'account-unavailable'
  | 'account-changed'
  | 'network-changed'
  | 'revision-changed'
  | 'scope-conflict';

export type WalletObservedNetwork = 'mainnet' | 'mutinynet' | 'regtest' | 'testnet-family' | 'unknown';

export interface WalletActionAccountRead {
  snapshot: WalletAccountSnapshot;
  observedNetwork: WalletObservedNetwork;
}

export class WalletActionInvalidatedError extends Error {
  readonly kind = 'wallet-action-invalidated' as const;

  constructor(
    readonly stage: WalletActionStage,
    readonly reason: WalletActionInvalidationReason,
  ) {
    super(`wallet-action-invalidated:${stage}:${reason}`);
    this.name = 'WalletActionInvalidatedError';
  }
}

export interface WalletActionGuardParams {
  snapshot: WalletAccountSnapshot;
  revision: number;
  getRevision: () => number;
  readCurrentSnapshot: () => Promise<WalletAccountSnapshot | WalletActionAccountRead | null>;
  prepareForAction?: () => Promise<void>;
}

export function observedWalletNetworkFromAddress(address: string): WalletObservedNetwork {
  const normalized = address.toLowerCase();
  if (normalized.startsWith('bcrt1')) return 'regtest';
  if (normalized.startsWith('tb1') || normalized.startsWith('m') || normalized.startsWith('n') || normalized.startsWith('2')) {
    return 'testnet-family';
  }
  if (normalized.startsWith('bc1') || normalized.startsWith('1') || normalized.startsWith('3')) return 'mainnet';
  return 'unknown';
}

export function isObservedWalletNetworkCompatible(configured: ChainNetwork, observed: WalletObservedNetwork): boolean {
  if (configured === 'main' || configured === 'alpha-mainnet') return observed === 'mainnet';
  if (configured === 'regtest') return observed === 'regtest';
  if (configured === 'mutiny') return observed === 'mutinynet' || observed === 'testnet-family';
  return observed === 'testnet-family';
}

function isAccountRead(value: WalletAccountSnapshot | WalletActionAccountRead): value is WalletActionAccountRead {
  return 'snapshot' in value;
}

function immutableSnapshot(snapshot: WalletAccountSnapshot): WalletAccountSnapshot {
  return Object.freeze({
    network: snapshot.network,
    walletType: snapshot.walletType,
    funds: Object.freeze({ ...snapshot.funds }),
    asset: Object.freeze({ ...snapshot.asset }),
    vault: Object.freeze({ ...snapshot.vault }),
  });
}

export class WalletActionGuard {
  readonly snapshot: WalletAccountSnapshot;
  readonly revision: number;
  readonly prepareForAction?: () => Promise<void>;
  private terminalError: WalletActionInvalidatedError | null = null;

  constructor(private readonly params: WalletActionGuardParams) {
    this.snapshot = immutableSnapshot(params.snapshot);
    this.revision = params.revision;
    if (params.prepareForAction) {
      const prepare = params.prepareForAction;
      this.prepareForAction = async () => {
        this.assertRevision('intent');
        await prepare();
        this.assertRevision('intent');
      };
    }
  }

  async assertFresh(stage: WalletActionStage): Promise<void> {
    if (this.terminalError) throw this.terminalError;
    this.assertRevision(stage);

    // This prompt-free read verifies the reviewed identity; it must never restore, align, or mutate the connector.
    const read = await waitForWalletRead(this.params.readCurrentSnapshot());
    this.assertRevision(stage);
    if (!read) this.fail(stage, 'account-unavailable');

    const current = isAccountRead(read) ? read.snapshot : read;
    const observedNetwork = isAccountRead(read) ? read.observedNetwork : observedWalletNetworkFromAddress(current.funds.address);
    if (
      (isAccountRead(read) || observedNetwork !== 'unknown') &&
      !isObservedWalletNetworkCompatible(this.snapshot.network, observedNetwork)
    ) {
      this.fail(stage, 'network-changed');
    }
    this.assertSnapshotEquality(stage, current);
  }

  assertSnapshot(stage: WalletActionStage, current: WalletAccountSnapshot): void {
    this.assertRevision(stage);
    this.assertSnapshotEquality(stage, current);
  }

  assertRevision(stage: WalletActionStage): void {
    if (this.terminalError) throw this.terminalError;
    if (this.params.getRevision() !== this.revision) this.fail(stage, 'revision-changed');
  }

  invalidate(stage: WalletActionStage, reason: WalletActionInvalidationReason): never {
    if (this.terminalError) throw this.terminalError;
    return this.fail(stage, reason);
  }

  private assertSnapshotEquality(stage: WalletActionStage, current: WalletAccountSnapshot): void {
    if (!walletAccountSnapshotsEqual(this.snapshot, current)) this.fail(stage, 'account-changed');
  }

  private fail(stage: WalletActionStage, reason: WalletActionInvalidationReason): never {
    this.terminalError = new WalletActionInvalidatedError(stage, reason);
    throw this.terminalError;
  }
}

const WALLET_ACTION_SCOPE_TOKEN = Symbol('wallet-action-scope-token');

export interface WalletActionScopeToken {
  readonly [WALLET_ACTION_SCOPE_TOKEN]: true;
}

interface WalletActionScope {
  guard: WalletActionGuard;
  tokens: Set<WalletActionScopeToken>;
}

const WALLET_ACTION_SCOPES = new WeakMap<object, WalletActionScope>();

export function enterWalletActionScope(wallet: ProtoWallet, guard: WalletActionGuard): WalletActionScopeToken {
  const current = WALLET_ACTION_SCOPES.get(wallet);
  if (current && current.guard !== guard) guard.invalidate('intent', 'scope-conflict');

  const token = Object.freeze({ [WALLET_ACTION_SCOPE_TOKEN]: true }) as WalletActionScopeToken;
  if (current) {
    current.tokens.add(token);
  } else {
    WALLET_ACTION_SCOPES.set(wallet, { guard, tokens: new Set([token]) });
  }
  return token;
}

export function releaseWalletActionScope(wallet: ProtoWallet, token: WalletActionScopeToken): void {
  const current = WALLET_ACTION_SCOPES.get(wallet);
  if (!current?.tokens.delete(token)) return;
  if (current.tokens.size === 0) WALLET_ACTION_SCOPES.delete(wallet);
}

export async function withWalletActionScope<T>(wallet: ProtoWallet, guard: WalletActionGuard, action: () => Promise<T>): Promise<T> {
  const token = enterWalletActionScope(wallet, guard);
  try {
    // Only connectors with an interactive preparation step need this boundary.
    // Passive freshness reads never unlock or change wallet permissions.
    if (guard.prepareForAction) await guard.prepareForAction();
    return await action();
  } finally {
    releaseWalletActionScope(wallet, token);
  }
}

export function getBoundWalletActionGuard(wallet: ProtoWallet): WalletActionGuard | undefined {
  return WALLET_ACTION_SCOPES.get(wallet)?.guard;
}

export async function assertWalletActionFresh(
  guard: WalletActionGuard | undefined,
  _wallet: ProtoWallet,
  stage: WalletActionStage,
): Promise<void> {
  if (!guard) return;
  await guard.assertFresh(stage);
}

export async function assertBoundWalletActionFresh(wallet: ProtoWallet, stage: WalletActionStage): Promise<void> {
  const guard = getBoundWalletActionGuard(wallet);
  if (guard) await guard.assertFresh(stage);
}
